import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, Signal, signal } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { formulaModelDefinition } from '../../../../entity-definitions/formula-model.definition';
import { polynomialModelDefinition } from '../../../../entity-definitions/polynomial-model.definition';
import { EntityDialogService } from '../../../../services/entity-dialog.service';
import { StoreInterfaceService } from '../../../../services/store-interface.service';
import { EntityDefinition } from '../../../../types/entity-definition.interface';
import {
  FormulaModel,
  FormulaModelsResolvedData,
  FormulaModelType,
  PolynomialModel,
  PolynomialModelsResolvedData,
} from '../../../../types/formula.interface';
import { Unit } from '../../../../types/unit.interface';
import { GenericParentGridComponent } from '../../../generic/generic-parent-grid.component';
import { CesButtonComponent } from '../../../ui/ces-buttons';
import { CesTitleComponent } from '../../../ui/ces-route-title/ces-route-title.component';

type FormulaLikeModel = FormulaModel | PolynomialModel;

interface FormulaLikeStoreApi<T extends FormulaLikeModel> {
  create(entity: T): Promise<T | null>;
  update(id: number, changes: Partial<T>): Promise<T | null>;
  delete(id: number): Promise<void>;
}

@Component({
  selector: 'app-formula-models-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, GenericParentGridComponent, CesTitleComponent, CesButtonComponent, TranslocoModule],
  templateUrl: './formula-models-list.component.html',
})
export class FormulaModelsListComponent<T extends FormulaLikeModel> {
  private readonly dialogService = inject(EntityDialogService);
  private readonly storeService = inject(StoreInterfaceService);
  private readonly translateService = inject(TranslocoService);

  private _formulaModelDefinition = formulaModelDefinition;
  private _polynomialModelDefinition = polynomialModelDefinition;
  private _resolvedData?: FormulaModelsResolvedData | PolynomialModelsResolvedData;
  private _formulaModels: Signal<T[]> = signal([]);
  private _units: Signal<Unit[]> = signal([]);
  private _dataLoaded: Signal<boolean> = signal(false);
  private _formulaModelType: FormulaModelType = FormulaModelType.FORMULA;

  @Input()
  set resolvedData(value: (FormulaModelsResolvedData | PolynomialModelsResolvedData) | undefined) {
    if (!value) return;

    this._resolvedData = value;
    this._formulaModels = value.formulaModels as unknown as Signal<T[]>;
    this._units = value.units;
    this._dataLoaded = value.dataLoaded;
    this._formulaModelType = value.formulaModelType;
  }

  get resolvedData(): FormulaModelsResolvedData | PolynomialModelsResolvedData | undefined {
    return this._resolvedData;
  }

  @Input()
  set models(value: Signal<T[]>) {
    if (value) {
      this._formulaModels = value;
    }
  }
  get models(): Signal<T[]> {
    return this._formulaModels;
  }

  @Input()
  set units(value: Signal<Unit[]>) {
    if (value) {
      this._units = value;
    }
  }
  get units(): Signal<Unit[]> {
    return this._units;
  }

  @Input()
  set dataLoaded(value: Signal<boolean>) {
    if (value) {
      this._dataLoaded = value;
    }
  }
  get dataLoaded(): Signal<boolean> {
    return this._dataLoaded;
  }

  @Input()
  set formulaModelType(value: FormulaModelType) {
    if (value) {
      this._formulaModelType = value;
    }
  }
  get formulaModelType(): FormulaModelType {
    return this._formulaModelType;
  }

  get store(): FormulaLikeStoreApi<T> {
    const concreteStore =
      this.formulaModelType === FormulaModelType.FORMULA
        ? this.storeService.getStore('FormulaModel')
        : this.storeService.getStore('PolynomialModel');
    return concreteStore as unknown as FormulaLikeStoreApi<T>;
  }

  get entityDefinition(): EntityDefinition<T> {
    const definition =
      this.formulaModelType === FormulaModelType.FORMULA ? this._formulaModelDefinition : this._polynomialModelDefinition;
    return definition as unknown as EntityDefinition<T>;
  }

  createModel(): void {
    this.dialogService
      .openCreateDialog<T>({
        header: this.entityDefinition.messages.dialogTitles.create(),
        entityDefinition: this.entityDefinition,
        mode: 'create',
        size: 'large',
        additionalData: {
          unitOptions: this.units(),
        },
      })
      .subscribe(formData => {
        if (!formData) return;
        if ('__action' in formData && formData.__action === 'delete') return;

        void this.store.create(formData as T).catch((error: unknown) => {
          console.error(this.translateService.translate('common.errors.modelCreationFailed'), error);
        });
      });
  }

  editModel(model: T): void {
    this.dialogService
      .openEditDialog<T>(model, {
        header: this.entityDefinition.messages.dialogTitles.edit(),
        headerParams: { name: model.name },
        entityDefinition: this.entityDefinition,
        mode: 'edit',
        size: 'large',
        additionalData: {
          unitOptions: this.units(),
        },
        onDelete: entity => this.deleteModel(entity),
      })
      .subscribe(result => {
        if (!result) return;
        if ('__action' in result && result.__action === 'delete') {
          this.deleteModel(result.entity as T);
          return;
        }
        void this.store.update(model.id, result as Partial<T>);
      });
  }

  deleteModel(model: T): void {
    void this.store.delete(model.id);
  }
}
